const YOUTUBE_TRIGGER_SELECTOR = ".iframeyoutube";
const DIRECT_IFRAME_SELECTOR =
  'iframe[src*="youtube.com/embed/"], iframe[src*="youtube-nocookie.com/embed/"]';

function extractVideoId(rawValue) {
  const value = String(rawValue ?? "").trim();

  if (!value) {
    return "";
  }

  if (/^[A-Za-z0-9_-]{11}$/.test(value)) {
    return value;
  }

  try {
    const url = new URL(value, window.location.origin);
    const host = url.hostname.toLowerCase();

    if (host.includes("youtu.be")) {
      const [candidate] = url.pathname.replace(/^\/+/, "").split("/");
      return /^[A-Za-z0-9_-]{11}$/.test(candidate) ? candidate : "";
    }

    if (
      !host.includes("youtube.com") &&
      !host.includes("youtube-nocookie.com")
    ) {
      return "";
    }

    const queryId = url.searchParams.get("v");
    if (queryId && /^[A-Za-z0-9_-]{11}$/.test(queryId)) {
      return queryId;
    }

    const segments = url.pathname.replace(/^\/+/, "").split("/");

    for (const prefix of ["embed", "shorts", "live"]) {
      const prefixIndex = segments.indexOf(prefix);
      const candidate = prefixIndex >= 0 ? segments[prefixIndex + 1] : "";

      if (/^[A-Za-z0-9_-]{11}$/.test(candidate)) {
        return candidate;
      }
    }
  } catch {
    return "";
  }

  return "";
}

function parseStartValue(rawValue) {
  const value = String(rawValue ?? "").trim();

  if (!value) {
    return 0;
  }

  if (/^\d+$/.test(value)) {
    return Number.parseInt(value, 10);
  }

  const clockMatch = value.match(/^(?:(\d+):)?(\d{1,2}):(\d{1,2})$/);
  if (clockMatch) {
    const hours = clockMatch[1] ? Number.parseInt(clockMatch[1], 10) : 0;
    const minutes = Number.parseInt(clockMatch[2], 10);
    const seconds = Number.parseInt(clockMatch[3], 10);

    return hours * 3600 + minutes * 60 + seconds;
  }

  const tokenMatch = value.match(/^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?$/i);
  if (tokenMatch) {
    const hours = tokenMatch[1] ? Number.parseInt(tokenMatch[1], 10) : 0;
    const minutes = tokenMatch[2] ? Number.parseInt(tokenMatch[2], 10) : 0;
    const seconds = tokenMatch[3] ? Number.parseInt(tokenMatch[3], 10) : 0;

    return hours * 3600 + minutes * 60 + seconds;
  }

  return 0;
}

function getThumbnailUrl(videoId, preferredSize = "hqdefault.jpg") {
  return `https://img.youtube.com/vi/${encodeURIComponent(videoId)}/${preferredSize}`;
}

function buildEmbedUrl(videoId, startTime = 0) {
  const url = new URL(
    `https://www.youtube-nocookie.com/embed/${encodeURIComponent(videoId)}`
  );

  url.searchParams.set("autoplay", "1");
  url.searchParams.set("rel", "0");
  url.searchParams.set("playsinline", "1");

  if (startTime > 0) {
    url.searchParams.set("start", String(startTime));
  }

  return url.toString();
}

function parseExistingEmbedUrl(rawValue, fallbackVideoId, fallbackStart) {
  const videoId = extractVideoId(rawValue) || fallbackVideoId;
  const startTimeFromUrl = (() => {
    try {
      const url = new URL(String(rawValue), window.location.origin);
      return parseStartValue(url.searchParams.get("start") || url.searchParams.get("t"));
    } catch {
      return 0;
    }
  })();

  return buildEmbedUrl(videoId, startTimeFromUrl || fallbackStart);
}

function hydratePreviewImage(image) {
  if (!image) {
    return;
  }

  image.loading = image.loading || "lazy";
  image.decoding = image.decoding || "async";

  if (image.dataset.src) {
    image.src = image.dataset.src;
    delete image.dataset.src;
  }

  if (image.dataset.srcset) {
    image.srcset = image.dataset.srcset;
    delete image.dataset.srcset;
  }
}

function createEmbedIframe({ embedUrl, title }) {
  const iframe = document.createElement("iframe");
  iframe.className = "yt-player-fabtheme";
  iframe.src = embedUrl;
  iframe.title = title;
  iframe.loading = "eager";
  iframe.allow =
    "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share";
  iframe.allowFullscreen = true;
  iframe.referrerPolicy = "strict-origin-when-cross-origin";

  return iframe;
}

function getTriggerMeta(trigger) {
  const videoId = extractVideoId(trigger.dataset.youtubeid || trigger.dataset.embedUrl);
  const startTime = parseStartValue(trigger.dataset.start || "0");
  const embedUrl = trigger.dataset.embedUrl
    ? parseExistingEmbedUrl(trigger.dataset.embedUrl, videoId, startTime)
    : buildEmbedUrl(videoId, startTime);
  const image = trigger.querySelector("img");
  const fallbackTitle = image?.alt?.trim() || "Riproduci video YouTube";
  const title = trigger.dataset.youtubeTitle || fallbackTitle;

  return { videoId, startTime, embedUrl, title };
}

function activateEmbed(trigger) {
  const { videoId, startTime, embedUrl, title } = getTriggerMeta(trigger);

  if (!videoId) {
    return;
  }

  const requestEvent = new CustomEvent("fabtheme:youtube-embed-request", {
    bubbles: true,
    cancelable: true,
    detail: {
      videoId,
      startTime,
      embedUrl,
      trigger,
    },
  });

  if (!trigger.dispatchEvent(requestEvent)) {
    return;
  }

  const iframe = createEmbedIframe({ embedUrl, title });
  trigger.replaceWith(iframe);

  iframe.dispatchEvent(
    new CustomEvent("fabtheme:youtube-embed-loaded", {
      bubbles: true,
      detail: {
        videoId,
        startTime,
        embedUrl,
      },
    })
  );
}

function bindTrigger(trigger) {
  if (!(trigger instanceof HTMLElement) || trigger.dataset.youtubeBound === "true") {
    return;
  }

  const { videoId, title } = getTriggerMeta(trigger);

  if (!videoId) {
    return;
  }

  trigger.dataset.youtubeid = videoId;
  trigger.dataset.youtubeBound = "true";
  trigger.setAttribute("role", "button");
  trigger.setAttribute("tabindex", "0");
  trigger.setAttribute("aria-label", title);

  hydratePreviewImage(trigger.querySelector("img"));

  trigger.addEventListener("click", () => {
    activateEmbed(trigger);
  });

  trigger.addEventListener("keydown", (event) => {
    if (event.key !== "Enter" && event.key !== " ") {
      return;
    }

    event.preventDefault();
    activateEmbed(trigger);
  });
}

function upgradeDirectIframe(iframe) {
  if (!(iframe instanceof HTMLIFrameElement)) {
    return;
  }

  const videoId = extractVideoId(iframe.src);

  if (!videoId) {
    return;
  }

  const startTime = (() => {
    try {
      const url = new URL(iframe.src, window.location.origin);
      return parseStartValue(url.searchParams.get("start") || url.searchParams.get("t"));
    } catch {
      return 0;
    }
  })();

  const trigger = document.createElement("div");
  trigger.className = "iframeyoutube";
  trigger.dataset.youtubeid = videoId;
  trigger.dataset.embedUrl = buildEmbedUrl(videoId, startTime);
  trigger.dataset.youtubeTitle =
    iframe.title?.trim() || "Riproduci video YouTube";

  if (startTime > 0) {
    trigger.dataset.start = String(startTime);
  }

  const image = document.createElement("img");
  image.className = "img-aspect-ratio";
  image.src = getThumbnailUrl(videoId);
  image.alt = iframe.title?.trim()
    ? `Anteprima ${iframe.title.trim()}`
    : "Anteprima video YouTube";
  image.width = 640;
  image.height = 360;
  image.loading = "lazy";
  image.decoding = "async";
  trigger.append(image);

  const wrapper = iframe.parentElement;
  wrapper?.classList.add("yt-embed");
  iframe.replaceWith(trigger);
}

export function initYoutubeLazyEmbeds(root = document) {
  if (!(root instanceof Document || root instanceof HTMLElement)) {
    return;
  }

  root.querySelectorAll(DIRECT_IFRAME_SELECTOR).forEach((iframe) => {
    if (iframe.closest(".wp-block-embed__wrapper")) {
      return;
    }

    upgradeDirectIframe(iframe);
  });

  root.querySelectorAll(YOUTUBE_TRIGGER_SELECTOR).forEach((trigger) => {
    bindTrigger(trigger);
  });
}
